'use client'

import { useEffect, useState } from 'react'

import Autoplay from 'embla-carousel-autoplay'

import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'

import { cn } from '@/lib/utils'

import type { PortfolioItem } from './portfolio-12'

type PortfolioContentProps = {
  portfolioItems: PortfolioItem[]
}

/**
 * Бүтээгдэхүүний карусель.
 *
 * ⚠️ Эх блок нь «гэрэлтүүлэг» хэлбэртэй байв: голын слайд том, хажуугийнх нь
 * жижиг (`basis-1/2 → 1/3 → 1/4 → 1/5`), төвд нь тэгшилж, БҮРХҮҮЛЭЭС ГАДАГШ
 * цухуйдаг. Улмаас хоёр талын бараа таллаж тасарч, нэрийг нь ч уншиж
 * болдоггүй байсан. Одоо сайтын нэг жишиг өргөний ДОТОР, слайд бүр ТЭНЦҮҮ
 * өргөнтэй — өргөн дэлгэц дээр яг 4 бараа бүтнээрээ багтана.
 *
 * Нэрийг зөвхөн идэвхтэй слайд дээр харуулдаг байсныг ч болиулав: одоо
 * бүх слайд бүтэн харагддаг тул нэг нь нэртэй, гурав нь нэргүй байх нь
 * учир дутагдалтай.
 */
const PortfolioContent = ({ portfolioItems }: PortfolioContentProps) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  /*
   * Эх хувилбар нь effect дотор ШУУД `setState` дуудаж, `off` бүртгэлээ ч
   * цуцалдаггүй байсан: (1) render бүрд нэмэлт дахин render үүсгэнэ,
   * (2) компонент дахин холбогдох бүрд сонсогч давхарлана.
   */
  useEffect(() => {
    if (!api) {
      return
    }

    const sync = () => setCurrent(api.selectedScrollSnap())

    api.on('select', sync)
    api.on('reInit', sync)

    return () => {
      api.off('select', sync)
      api.off('reInit', sync)
    }
  }, [api])

  return (
    <div>
      <Carousel
        setApi={setApi}
        opts={{
          /* Слайдууд тэнцүү тул төвд нь биш, ЭХЛЭЛД нь тэгшилнэ */
          align: 'start',
          loop: true,
          slidesToScroll: 1
        }}
        plugins={[
          /*
            Эх блок нь 2 секунд тутам гүйдэг байсан — барааны нэр уншиж
            амжихаас өмнө солигдоно. `stopOnMouseEnter` нь тодорхой барааг
            ажиглах гэсэн хүнийг хөөцөлдүүлэхгүй.
          */
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
            stopOnFocusIn: false,
            stopOnMouseEnter: true
          })
        ]}
      >
        <CarouselContent className='-ml-4'>
          {portfolioItems.map(item => (
            <CarouselItem key={item.id} className='basis-full cursor-grab pl-4 sm:basis-1/2 md:basis-1/3 lg:basis-1/4'>
              <div className='w-full'>
                {/*
                  Барааны зураг нь харилцан адилгүй харьцаатай (лааз, уут, сав)
                  бөгөөд ихэвчлэн цагаан/тунгалаг дэвсгэртэй. Тогтмол харьцаа +
                  `object-contain` нь бүтээгдэхүүнийг бүтнээр нь харуулж,
                  слайдын өндрийг жигдрүүлнэ.

                  Дэвсгэрийг цагаан үлдээв: саарал хавтан нь цагаан дэвсгэртэй
                  барааг бохирдуулж харагдуулдаг.
                */}
                <div className='relative aspect-square overflow-hidden rounded-lg bg-white'>
                  <img src={item.image} alt={item.title} className='h-full w-full rounded-lg object-contain p-4' />
                </div>

                <div className='mt-3 space-y-1 text-center'>
                  <h3 className='text-xl font-medium text-balance lg:text-xl'>{item.title}</h3>
                  {item.description && <p className='text-muted-foreground text-sm text-pretty'>{item.description}</p>}
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Navigation Dots */}
      <div className='mt-8 flex flex-wrap justify-center gap-2'>
        {portfolioItems.map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              'size-3.25 rounded-full transition-all duration-300',
              index === current ? 'bg-primary scale-125' : 'bg-muted hover:bg-muted-foreground/60'
            )}
            aria-label={`${index + 1}-р бүтээгдэхүүн рүү очих`}
          />
        ))}
      </div>
    </div>
  )
}

export default PortfolioContent
